import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Card, CopyButton, cx } from '../../components/ui';
import { api, type TemplateSummary } from '../../lib/api';
import { usePageTitle } from '../../lib/usePageTitle';
import { PanelState } from './DashboardLayout';

/**
 * Names that title-casing gets wrong. `readme.md` would become `Readme`, and
 * the file everyone knows is spelled README.
 */
const SPECIAL_NAMES: Record<string, string> = {
  'readme.md': 'README',
};

/**
 * Where each document belongs, because the folder is part of the instruction.
 *
 * The README describes the product, so one sits at the project root. The
 * feature spec describes a single feature and lives under `features/<name>/` —
 * saving it to the project root instead would mean a second feature had
 * nowhere to put its own, which is the mistake this line exists to prevent.
 */
const LOCATION: Record<string, string> = {
  'readme.md': 'your project folder',
  'feature-spec.md': 'the feature folder, e.g. features/Feature-01/',
};

/**
 * Turn `feature-spec.md` into `Feature Spec`, for the tab label.
 *
 * The filename is the identity — it is what the agent looks for and what the
 * operator saves — so it stays visible in the panel. The tab just needs to be
 * readable at a glance.
 */
function label(document: string): string {
  const special = SPECIAL_NAMES[document];
  if (special) return special;

  return document
    .replace(/\.md$/, '')
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * The QA document templates, one tab each, with a copy button.
 *
 * Read-only. The agent deliberately does not seed a project with these — an
 * unedited template on disk is indistinguishable from a document someone began
 * and abandoned, and verification refuses to run against either. So they are
 * shown here to be copied and filled in knowingly.
 *
 * Which templates are *required* comes from the API rather than a list in this
 * file. The endpoint is what tells a client which documents a run reads, and a
 * hardcoded copy here would be a second place to update when that changes.
 *
 * Bodies are fetched per tab rather than all at once: together they are around
 * 33 KB of markdown, and a panel should not pull all of it to show one.
 */
export function Templates() {
  usePageTitle('Templates — WaveQA');

  const [selected, setSelected] = useState<string | null>(null);

  const listing = useQuery({
    queryKey: ['templates'],
    queryFn: api.getTemplates,
    // Templates change on deploy, not on user action, so there is nothing here
    // to invalidate them.
    staleTime: 5 * 60_000,
  });

  // Open the first tab once the listing arrives. Done in an effect rather than
  // during render so the choice is not recomputed on every re-render, and so a
  // tab the user picked is never overridden by a refetch.
  useEffect(() => {
    if (selected === null && listing.data?.length) {
      setSelected(listing.data[0]!.filename);
    }
  }, [listing.data, selected]);

  const active: TemplateSummary | undefined = listing.data?.find(
    (item) => item.filename === selected,
  );

  const content = useQuery({
    queryKey: ['template', selected],
    queryFn: () => api.getTemplate(selected!),
    enabled: selected !== null,
    staleTime: 5 * 60_000,
  });

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-white">QA document templates</h2>
        <p className="mt-1 max-w-2xl text-sm text-hull-400">
          These two are the whole of what you write. Copy a template, fill it in
          with your product's real detail, and save it under the filename shown.
          The README describes the product and lives in the project folder; the
          Feature Spec describes one feature and lives in that feature's folder,
          so two features never overwrite each other.
        </p>
      </div>

      <Alert tone="info">
        <strong className="text-white">
          These two are the whole input to a run.
        </strong>{' '}
        WaveQA reads nothing else about your product.
        <span className="mt-2 block text-hull-400">
          They are references, not starting files. The agent does not
          create them for you on purpose: an unedited template on disk looks
          exactly like a document someone started and abandoned, and
          verification refuses to run against either. Filling one in is the step
          that makes a run mean something.
        </span>
      </Alert>

      <PanelState
        loading={listing.isPending}
        error={listing.error}
        empty={listing.data?.length === 0}
        emptyTitle="No templates available"
        emptyBody="The API did not return any templates. Try again shortly."
      >
        <div className="mt-5">
          <div
            role="tablist"
            aria-label="QA document templates"
            className="flex flex-wrap gap-2"
          >
            {listing.data?.map((template) => (
              <button
                key={template.filename}
                role="tab"
                type="button"
                aria-selected={selected === template.filename}
                onClick={() => setSelected(template.filename)}
                className={cx(
                  'rounded-lg border px-4 py-2 text-sm transition',
                  selected === template.filename
                    ? 'border-surge-500 bg-surge-500/10 font-medium text-white'
                    : 'border-hull-600 text-hull-300 hover:border-hull-500 hover:text-white',
                )}
              >
                {label(template.document)}
                {!template.required && (
                  <span className="ml-2 text-[11px] text-hull-500">ref</span>
                )}
              </button>
            ))}
          </div>

          {active && (
            <Card className="mt-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-white">
                    {label(active.document)}
                  </h3>
                  <p className="mt-1 text-xs text-hull-400">
                    Save as{' '}
                    <code className="font-mono text-surge-400">{active.document}</code>{' '}
                    in {LOCATION[active.document] ?? 'your project folder'}
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <span
                    className={cx(
                      'rounded-full border px-2 py-0.5 text-[11px]',
                      active.required
                        ? 'border-surge-600/40 text-surge-400'
                        : 'border-hull-600 text-hull-400',
                    )}
                  >
                    {active.required ? 'required' : 'reference'}
                  </span>

                  {/*
                    Rendered only once the body has arrived. A copy button that
                    is present but silently copies nothing is worse than one
                    that appears a moment later.
                  */}
                  {content.data && (
                    <CopyButton
                      value={content.data.content}
                      label={`Copy ${active.document}`}
                    />
                  )}
                </div>
              </div>

              {content.isPending && (
                <p className="mt-4 text-sm text-hull-400">Loading template…</p>
              )}

              {content.error && (
                <div className="mt-4">
                  <Alert tone="error">
                    That template could not be loaded. Try selecting it again.
                  </Alert>
                </div>
              )}

              {content.data && (
                <>
                  {/*
                    `overflow-x-auto` on the pre, not the page: these templates
                    contain wide markdown tables, and a dashboard whose whole
                    body scrolls sideways is a broken dashboard.
                  */}
                  <pre className="mt-4 max-h-128 overflow-auto rounded-lg border border-hull-700 bg-hull-950 p-4 text-xs leading-relaxed text-hull-200">
                    <code>{content.data.content}</code>
                  </pre>

                  <p className="mt-3 text-xs text-hull-500">
                    {content.data.content.split('\n').length} lines ·{' '}
                    {(content.data.sizeBytes / 1024).toFixed(1)} KB · sha256{' '}
                    <code className="font-mono">{content.data.sha256.slice(0, 12)}…</code>
                  </p>
                </>
              )}
            </Card>
          )}
        </div>
      </PanelState>
    </div>
  );
}
